import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import SalesSidebar from "@/components/sidebar/SalesSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { ChevronRight as BreadcrumbChevron } from "lucide-react";
import ReferralShareKit from "@/components/sales/ReferralShareKit";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import {
  Package,
  Search,
  RefreshCw,
  Eye,
  Plus,
  StickyNote,
  Clock,
  CheckCircle,
  XCircle,
  RotateCcw,
  Briefcase,
  PanelLeft,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import SalesOverview from "@/components/sales/SalesOverview";
import ContractorTaxInfo from "@/components/ContractorTaxInfo";
import PatientPipeline, { type PatientRecord, type PatientStatus } from "@/components/sales/PatientPipeline";
import PayoutHistory from "@/components/sales/PayoutHistory";
import type { ActionItem } from "@/components/sales/ActionQueue";

interface Order {
  id: string;
  patient_id: string | null;
  order_type: string;
  status: string;
  prescription_id: string | null;
  amount_cents: number;
  currency: string;
  patient_email: string | null;
  patient_name: string | null;
  salesperson_id: string | null;
  commission_rate: number;
  created_at: string;
  updated_at: string;
}

interface OrderNote {
  id: string;
  order_id: string;
  admin_id: string;
  note: string;
  created_at: string;
}

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; icon: any }> = {
  pending: { label: "Pending", variant: "secondary", icon: Clock },
  active: { label: "Active", variant: "default", icon: CheckCircle },
  fulfilled: { label: "Fulfilled", variant: "outline", icon: Package },
  cancelled: { label: "Cancelled", variant: "destructive", icon: XCircle },
  refunded: { label: "Refunded", variant: "destructive", icon: RotateCcw },
};

const formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`;

function derivePatientStatus(orders: Order[]): PatientStatus {
  if (orders.some((o) => o.status === "active")) return "active";
  if (orders.every((o) => ["cancelled", "refunded"].includes(o.status)) && orders.length > 0) return "churned";
  if (orders.every((o) => o.status === "pending")) return "pending";
  return "inactive";
}

function buildPatients(orders: Order[]): PatientRecord[] {
  const map = new Map<string, Order[]>();
  orders.forEach((o) => {
    const key = o.patient_email || o.id; // fallback to order id if no email
    const list = map.get(key) || [];
    list.push(o);
    map.set(key, list);
  });

  return Array.from(map.entries()).map(([email, ords]) => {
    const sorted = [...ords].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const activeOrds = ords.filter((o) => !["cancelled", "refunded"].includes(o.status));
    return {
      email,
      name: sorted[0].patient_name || "Unknown",
      status: derivePatientStatus(ords),
      totalSpent: activeOrds.reduce((s, o) => s + o.amount_cents, 0),
      orderCount: ords.length,
      lastOrderDate: sorted[0].created_at,
      orders: sorted.map((o) => ({
        id: o.id,
        status: o.status,
        amount_cents: o.amount_cents,
        order_type: o.order_type,
        created_at: o.created_at,
      })),
    };
  });
}

function buildActionItems(orders: Order[], patients: PatientRecord[]): ActionItem[] {
  const items: ActionItem[] = [];
  const now = new Date();

  // Pending orders > 3 days
  orders
    .filter((o) => o.status === "pending" && differenceInDays(now, new Date(o.created_at)) > 3)
    .forEach((o) => {
      items.push({
        id: `pending-${o.id}`,
        priority: "high",
        label: `Pending order for ${o.patient_name || "unknown"} needs follow-up`,
        patientEmail: o.patient_email,
        type: "pending_followup",
        createdAt: o.created_at,
      });
    });

  // Cancelled subscriptions → win-back
  orders
    .filter((o) => o.status === "cancelled" && o.order_type === "subscription")
    .forEach((o) => {
      items.push({
        id: `cancelled-${o.id}`,
        priority: "high",
        label: `Win back ${o.patient_name || "patient"} — subscription cancelled`,
        patientEmail: o.patient_email,
        type: "cancelled_winback",
        createdAt: o.created_at,
      });
    });

  // Inactive patients > 30 days
  patients
    .filter((p) => {
      if (p.status === "churned") return false;
      return differenceInDays(now, new Date(p.lastOrderDate)) > 30;
    })
    .forEach((p) => {
      items.push({
        id: `inactive-${p.email}`,
        priority: "medium",
        label: `Re-engage ${p.name} — inactive 30+ days`,
        patientEmail: p.email,
        type: "inactive",
        createdAt: p.lastOrderDate,
      });
    });

  // New orders today
  orders
    .filter((o) => differenceInDays(now, new Date(o.created_at)) === 0 && o.status === "pending")
    .forEach((o) => {
      // avoid duplicate with pending_followup
      if (!items.some((i) => i.id === `pending-${o.id}`)) {
        items.push({
          id: `new-${o.id}`,
          priority: "low",
          label: `Review new order for ${o.patient_name || "patient"}`,
          patientEmail: o.patient_email,
          type: "new_order",
          createdAt: o.created_at,
        });
      }
    });

  return items;
}

const SalesDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { isSalesperson, isAdmin, loading: roleLoading } = useUserRole();
  const { toast } = useToast();
  const [sidebarTab, setSidebarTab] = useState("overview");

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [notes, setNotes] = useState<OrderNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({
    patient_email: "",
    patient_name: "",
    order_type: "subscription",
    amount_cents: 0,
    status: "pending",
  });

  const [referralClicks, setReferralClicks] = useState(0);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("salesperson_orders")
      .select("*")
      .eq("salesperson_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error loading orders", description: error.message, variant: "destructive" });
    } else {
      setOrders((data as Order[]) || []);
    }

    // Fetch referral click count
    const { count } = await supabase
      .from("referral_clicks")
      .select("*", { count: "exact", head: true })
      .eq("referral_code", user.id);
    setReferralClicks(count ?? 0);

    setLoading(false);
  }, [user, toast]);

  useEffect(() => {
    if (user && isSalesperson) fetchOrders();
  }, [user, isSalesperson, fetchOrders]);

  // Derived data
  const patients = useMemo(() => buildPatients(orders), [orders]);
  const actionItems = useMemo(() => buildActionItems(orders, patients), [orders, patients]);

  const activeOrders = useMemo(() => orders.filter((o) => !["cancelled", "refunded"].includes(o.status)), [orders]);
  const totalRevenue = useMemo(() => activeOrders.reduce((s, o) => s + o.amount_cents, 0), [activeOrders]);
  const totalCommissions = useMemo(() => activeOrders.reduce((s, o) => s + (o.amount_cents * o.commission_rate) / 100, 0), [activeOrders]);
  const activePatientCount = useMemo(() => patients.filter((p) => p.status === "active").length, [patients]);
  const churnedPatientCount = useMemo(() => patients.filter((p) => p.status === "churned").length, [patients]);
  const conversionRate = useMemo(() => {
    if (patients.length === 0) return 0;
    return Math.round((activePatientCount / patients.length) * 100);
  }, [patients, activePatientCount]);

  // Monthly chart
  const barData = useMemo(() => {
    const monthlyData: Record<string, number> = {};
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toLocaleString("default", { month: "short" });
      monthlyData[key] = 0;
    }
    activeOrders.forEach((o) => {
      const d = new Date(o.created_at);
      const key = d.toLocaleString("default", { month: "short" });
      if (key in monthlyData) monthlyData[key] += o.amount_cents;
    });
    return Object.entries(monthlyData).map(([month, cents]) => ({ month, revenue: cents / 100 }));
  }, [activeOrders]);

  // Orders tab filtering
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (statusFilter !== "all") result = result.filter((o) => o.status === statusFilter);
    const term = search.toLowerCase();
    if (term) {
      result = result.filter((o) =>
        (o.patient_name?.toLowerCase().includes(term) || o.patient_email?.toLowerCase().includes(term) || o.id.includes(term)) ?? true
      );
    }
    return result;
  }, [orders, statusFilter, search]);

  const fetchNotes = async (orderId: string) => {
    setNotesLoading(true);
    const { data } = await supabase
      .from("order_notes")
      .select("*")
      .eq("order_id", orderId)
      .order("created_at", { ascending: false });
    setNotes((data as OrderNote[]) || []);
    setNotesLoading(false);
  };

  const openDetail = (order: Order) => {
    setSelectedOrder(order);
    fetchNotes(order.id);
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedOrder || !user) return;
    const { error } = await supabase.from("order_notes").insert({
      order_id: selectedOrder.id,
      admin_id: user.id,
      note: newNote.trim(),
    });
    if (error) {
      toast({ title: "Failed to add note", description: error.message, variant: "destructive" });
    } else {
      setNewNote("");
      fetchNotes(selectedOrder.id);
      toast({ title: "Note added" });
    }
  };

  const updateStatus = async (orderId: string, newStatus: string) => {
    const { error } = await supabase
      .from("orders")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", orderId);
    if (error) {
      toast({ title: "Failed to update status", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Status updated" });
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
    }
  };

  const createOrder = async () => {
    if (!user) return;
    // Fetch commission rate from settings, fallback to 10
    let rate = 10;
    const { data: settings } = await supabase
      .from("salesperson_settings")
      .select("commission_rate")
      .eq("user_id", user.id)
      .maybeSingle();
    if (settings?.commission_rate != null) rate = Number(settings.commission_rate);

    const { error } = await supabase.from("orders").insert({
      patient_email: newOrder.patient_email || null,
      patient_name: newOrder.patient_name || null,
      order_type: newOrder.order_type,
      amount_cents: newOrder.amount_cents,
      status: newOrder.status,
      salesperson_id: user.id,
      commission_rate: rate,
    });
    if (error) {
      toast({ title: "Failed to create order", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Order created" });
      setCreateOpen(false);
      setNewOrder({ patient_email: "", patient_name: "", order_type: "subscription", amount_cents: 0, status: "pending" });
      fetchOrders();
    }
  };

  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isSalesperson && !isAdmin) return <Navigate to="/dashboard" replace />;

  const referralLink = `${window.location.origin}/r/${user?.id}`;

  const activeTab = sidebarTab;
  const setActiveTab = setSidebarTab;

  const TAB_LABELS: Record<string, string> = {
    overview: "Overview",
    patients: "Patients",
    orders: "Orders",
    payouts: "Payouts",
    referrals: "Referrals",
    tax: "Tax Info",
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-16">
        <SidebarProvider>
          <div className="min-h-[calc(100vh-4rem)] flex w-full">
            <SalesSidebar activeTab={activeTab} onNavigateToTab={setActiveTab} />
            <main className="flex-1 overflow-auto">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <SidebarTrigger className="shrink-0">
                    <PanelLeft size={18} />
                  </SidebarTrigger>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Briefcase size={18} className="text-primary" />
                    </div>
                    <div>
                      <h1 className="font-display text-2xl text-foreground leading-tight">Sales Dashboard</h1>
                      {activeTab !== "overview" && (
                        <nav className="flex items-center gap-1.5 text-xs mt-0.5">
                          <button
                            onClick={() => setActiveTab("overview")}
                            className="text-muted-foreground hover:text-primary transition-colors font-medium"
                          >
                            Overview
                          </button>
                          <BreadcrumbChevron size={12} className="text-muted-foreground/50" />
                          <span className="text-foreground font-medium">
                            {TAB_LABELS[activeTab] || activeTab}
                          </span>
                        </nav>
                      )}
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="flex justify-center py-24">
                    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {activeTab === "overview" && (
                      <ErrorBoundary label="Overview tab">
                        <SalesOverview
                          totalRevenue={totalRevenue}
                          totalCommissions={totalCommissions}
                          activePatients={activePatientCount}
                          churnedPatients={churnedPatientCount}
                          conversionRate={conversionRate}
                          barData={barData}
                          actionItems={actionItems}
                          referralLink={referralLink}
                          formatAmount={formatAmount}
                          referralClicks={referralClicks}
                        />
                      </ErrorBoundary>
                    )}

                    {activeTab === "patients" && (
                      <ErrorBoundary label="Patients tab">
                        <PatientPipeline patients={patients} formatAmount={formatAmount} />
                      </ErrorBoundary>
                    )}

                    {activeTab === "orders" && (
                      <ErrorBoundary label="Orders tab">
                        <Card className="border-border mb-4">
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                              <div className="relative flex-1 w-full">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input placeholder="Search orders..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                              </div>
                              <div className="flex items-center gap-2">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                  <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="fulfilled">Fulfilled</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                    <SelectItem value="refunded">Refunded</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button variant="outline" size="sm" onClick={fetchOrders} className="gap-1">
                                  <RefreshCw size={14} /> Refresh
                                </Button>
                                <Button size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
                                  <Plus size={14} /> New Order
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="border-border">
                          <CardContent className="p-0">
                            {filteredOrders.length === 0 ? (
                              <p className="text-center text-muted-foreground py-16">No orders found</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Order</TableHead>
                                      <TableHead>Patient</TableHead>
                                      <TableHead>Type</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Amount</TableHead>
                                      <TableHead>Commission</TableHead>
                                      <TableHead>Date</TableHead>
                                      <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {filteredOrders.map((order) => {
                                      const sc = statusConfig[order.status] || statusConfig.pending;
                                      const StatusIcon = sc.icon;
                                      const commission = (order.amount_cents * order.commission_rate) / 100;
                                      return (
                                        <TableRow key={order.id} className="cursor-pointer hover:bg-secondary/30" onClick={() => openDetail(order)}>
                                          <TableCell className="font-mono text-xs text-muted-foreground">{order.id.slice(0, 8)}…</TableCell>
                                          <TableCell>
                                            <p className="text-sm font-medium">{order.patient_name || "—"}</p>
                                          </TableCell>
                                          <TableCell>
                                            <Badge variant="secondary" className="text-xs capitalize">{order.order_type}</Badge>
                                          </TableCell>
                                          <TableCell>
                                            <Badge variant={sc.variant} className="text-xs gap-1"><StatusIcon size={12} /> {sc.label}</Badge>
                                          </TableCell>
                                          <TableCell className="font-medium">{formatAmount(order.amount_cents)}</TableCell>
                                          <TableCell className="text-primary font-medium">{formatAmount(commission)}</TableCell>
                                          <TableCell className="text-xs text-muted-foreground">{format(new Date(order.created_at), "MMM d, yyyy")}</TableCell>
                                          <TableCell className="text-right">
                                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDetail(order); }}>
                                              <Eye size={14} />
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </ErrorBoundary>
                    )}

                    {activeTab === "payouts" && (
                      <ErrorBoundary label="Payouts tab">
                        <PayoutHistory />
                      </ErrorBoundary>
                    )}

                    {activeTab === "referrals" && (
                      <ErrorBoundary label="Referrals tab">
                        <ReferralShareKit referralLink={referralLink} />
                      </ErrorBoundary>
                    )}

                    {activeTab === "tax" && (
                      <ErrorBoundary label="Tax info tab">
                        <ContractorTaxInfo />
                      </ErrorBoundary>
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        </SidebarProvider>
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedOrder && (() => {
            const commission = (selectedOrder.amount_cents * selectedOrder.commission_rate) / 100;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-xl flex items-center gap-2">
                    <Package size={18} /> Order Details
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-2">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Order ID</span>
                      <p className="font-mono text-xs mt-0.5">{selectedOrder.id}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Type</span>
                      <p className="capitalize mt-0.5">{selectedOrder.order_type}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Patient</span>
                      <p className="mt-0.5">{selectedOrder.patient_name || "—"}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Amount</span>
                      <p className="font-semibold mt-0.5">{formatAmount(selectedOrder.amount_cents)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Commission ({selectedOrder.commission_rate}%)</span>
                      <p className="font-semibold text-primary mt-0.5">{formatAmount(commission)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created</span>
                      <p className="mt-0.5">{format(new Date(selectedOrder.created_at), "MMM d, yyyy h:mm a")}</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm text-muted-foreground mb-1.5 block">Update Status</label>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(statusConfig).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                          <Button key={key} variant={selectedOrder.status === key ? "default" : "outline"} size="sm" className="gap-1 text-xs"
                            onClick={() => updateStatus(selectedOrder.id, key)} disabled={selectedOrder.status === key}>
                            <Icon size={12} /> {cfg.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <StickyNote size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium">Notes</span>
                    </div>
                    <div className="flex gap-2 mb-3">
                      <Textarea placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} className="min-h-[60px] text-sm" />
                      <Button onClick={addNote} disabled={!newNote.trim()} className="shrink-0 self-end">Add</Button>
                    </div>
                    {notesLoading ? (
                      <div className="flex justify-center py-4">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : notes.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No notes yet</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {notes.map((n) => (
                          <div key={n.id} className="p-3 rounded-lg bg-secondary/50 border border-border">
                            <p className="text-sm">{n.note}</p>
                            <p className="text-xs text-muted-foreground mt-1">{format(new Date(n.created_at), "MMM d, h:mm a")}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Create New Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Patient Name</label>
              <Input value={newOrder.patient_name} onChange={(e) => setNewOrder((p) => ({ ...p, patient_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Patient Email</label>
              <Input type="email" value={newOrder.patient_email} onChange={(e) => setNewOrder((p) => ({ ...p, patient_email: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Order Type</label>
              <Select value={newOrder.order_type} onValueChange={(v) => setNewOrder((p) => ({ ...p, order_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="subscription">Subscription</SelectItem>
                  <SelectItem value="prescription">Prescription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Initial Status</label>
              <Select value={newOrder.status} onValueChange={(v) => setNewOrder((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Amount ($)</label>
              <Input type="number" step="0.01" min="0" value={(newOrder.amount_cents / 100).toFixed(2)}
                onChange={(e) => setNewOrder((p) => ({ ...p, amount_cents: Math.round(parseFloat(e.target.value || "0") * 100) }))} />
            </div>
            <Button onClick={createOrder} className="w-full">Create Order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesDashboard;
